import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Tooltip } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Viewer from 'components/DicomMediaViewer';
import Segments, { SegmentationPayload } from 'components/Labels/segments';
import Prediction from 'components/Predictions/predict';
import EncephalonSection from 'components/Encephalon';
import { useEncephalon } from 'contexts/EncephalonContext';
import {
  useGetDicomDataQuery,
  useGetDicomsInStudyQuery,
  useGetEncephaloneStudyDataQuery,
} from 'services/dicomService';
import { useGetStudyDetailQuery, type DicomRow, type ReportSummary } from 'services/exploreService';
import { Empty, Skeleton, ViewChips, annotationMarks, fmt, studyHref } from 'components/explore';
import { deriveExtension, measurementName, sectionName } from './labels';
import './dicompage.css';

export const dicomHref = (icid: string) => `/dicoms/${encodeURIComponent(icid)}`;

type KV = [string, React.ReactNode];

/** Two-column descriptor table: label on the left, value on the right. */
const KVTable: React.FC<{ rows: KV[]; caption?: string }> = ({ rows, caption }) => (
  <table className="ax-dtable">
    {caption && <caption>{caption}</caption>}
    <tbody>
      {rows.map(([k, v]) => (
        <tr key={k}>
          <th scope="row">{k}</th>
          <td>
            {v === null || v === undefined || v === '' ? <span className="ax-faint">–</span> : v}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Section: React.FC<{ title: string; extra?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  extra,
  children,
}) => (
  <section className="ax-dsection">
    <header>
      <h3>{title}</h3>
      {extra}
    </header>
    {children}
  </section>
);

const yes = (v: boolean | null | undefined) =>
  v ? <span style={{ color: 'var(--good)' }}>yes</span> : <span className="ax-faint">no</span>;

/** One DICOM in the study rail. */
const RailItem = React.memo<{ d: DicomRow; current: boolean; onPick: (icid: string) => void }>(
  ({ d, current, onPick }) => {
    const [srcIdx, setSrcIdx] = useState(0);
    const sources = [d.media.poster, d.media.still].filter(Boolean) as string[];
    const ref = useRef<HTMLButtonElement | null>(null);
    useEffect(() => {
      if (current) ref.current?.scrollIntoView({ block: 'nearest' });
    }, [current]);
    return (
      <button
        ref={ref}
        type="button"
        className={`ax-rail-dicom ${current ? 'sel' : ''}`}
        aria-current={current ? 'true' : undefined}
        onClick={() => onPick(d.icid)}
        title={d.icid}
      >
        <span className="thumb">
          {sources[srcIdx] ? (
            <img
              src={sources[srcIdx]}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={() => setSrcIdx(i => i + 1)}
            />
          ) : (
            <span className="ph">{(d.number_of_frames ?? 0) > 1 ? 'cine' : 'still'}</span>
          )}
          {d.index != null && <b className="idx">#{d.index}</b>}
        </span>
        <span className="info">
          <span className="view">{d.labeled_view || d.predicted_view || 'No view'}</span>
          <span className="sub">
            {d.type ?? '–'} · {d.number_of_frames ?? '–'}f
            {annotationMarks(d).length > 0 && <> · {annotationMarks(d).join(' ')}</>}
          </span>
        </span>
      </button>
    );
  }
);
RailItem.displayName = 'RailItem';

const ReportTables: React.FC<{ report: ReportSummary }> = ({ report }) => {
  const [showSections, setShowSections] = useState(false);
  return (
    <>
      <Section
        title="Report"
        extra={
          <span className="ax-faint" style={{ fontSize: 12 }}>
            {[
              report.signer,
              report.date_created && new Date(report.date_created).toLocaleDateString(),
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        }
      >
        {report.measurements.length ? (
          <table className="ax-dtable">
            <caption>Measurements</caption>
            <tbody>
              {report.measurements.map(m => (
                <tr key={m.key}>
                  <th scope="row">{measurementName(m.key)}</th>
                  <td>
                    {String(m.value)}
                    {m.unit && <span className="ax-faint"> {m.unit}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="ax-faint">No measurements recorded.</p>
        )}
      </Section>
      {report.findings.length > 0 && (
        <Section title={`Findings (${report.findings.length})`}>
          <table className="ax-dtable findings">
            <thead>
              <tr>
                <th>Finding</th>
                <th>Region</th>
                <th>Condition</th>
                <th>Severity</th>
              </tr>
            </thead>
            <tbody>
              {report.findings.map((f, i) => (
                <tr key={i}>
                  <td>{f.text || f.description}</td>
                  <td>{f.region ?? <span className="ax-faint">–</span>}</td>
                  <td>
                    {f.condition ? (
                      f.condition.replace(/_/g, ' ')
                    ) : (
                      <span className="ax-faint">–</span>
                    )}
                  </td>
                  <td>{f.stratification ?? <span className="ax-faint">–</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
      {report.sections.length > 0 && (
        <Section
          title="Report text"
          extra={
            <button type="button" className="ax-textbtn" onClick={() => setShowSections(s => !s)}>
              {showSections ? 'Hide' : `Show ${report.sections.length} sections`}
            </button>
          }
        >
          {showSections ? (
            <table className="ax-dtable text">
              <tbody>
                {report.sections.map(s => (
                  <tr key={s.key}>
                    <th scope="row">{sectionName(s.key)}</th>
                    <td>{s.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            report.sections.find(s => s.key === 'conclusions') && (
              <p className="ax-note" style={{ whiteSpace: 'pre-wrap' }}>
                {report.sections.find(s => s.key === 'conclusions')!.text}
              </p>
            )
          )}
        </Section>
      )}
    </>
  );
};

/**
 * One DICOM in its study: the study's DICOMs on the left (←/→ to move), the clip with
 * overlays in the middle with labels and predictions below it, descriptor tables on the right.
 */
const DicomPage: React.FC = () => {
  const { icid = '' } = useParams();
  const navigate = useNavigate();
  const {
    data: dicom,
    isFetching: dicomLoading,
    error: dicomError,
  } = useGetDicomDataQuery(icid, { skip: !icid });
  const studyId: string = dicom?.study_id ?? '';
  const { data: detail } = useGetStudyDetailQuery(studyId, { skip: !studyId });
  const { data: dicomsInStudy } = useGetDicomsInStudyQuery(studyId, { skip: !studyId });

  const [selectedSegmentation, setSelectedSegmentation] = useState<SegmentationPayload | null>(
    null
  );
  const [selectedKeypointFrame, setSelectedKeypointFrame] = useState<null | {
    frameUrl: string;
    frameIcid: string;
    frameExtension: string;
    keypoints: any[];
  }>(null);

  useEffect(() => {
    setSelectedSegmentation(null);
    setSelectedKeypointFrame(null);
  }, [icid]);

  const rail = detail?.dicoms ?? [];
  const pos = rail.findIndex(d => d.icid === icid);
  const row = pos >= 0 ? rail[pos] : undefined;
  const go = useCallback((target?: string) => target && navigate(dicomHref(target)), [navigate]);
  const prev = pos > 0 ? rail[pos - 1].icid : undefined;
  const next = pos >= 0 && pos < rail.length - 1 ? rail[pos + 1].icid : undefined;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        if (prev) {
          e.preventDefault();
          go(prev);
        }
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        if (next) {
          e.preventDefault();
          go(next);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next, go]);

  // Encephalon export state follows the study, as on the previous page.
  const { setStudyExists, setScans, setCurrentStudyUuid, currentStudyUuid, triggerScanUpdate } =
    useEncephalon();
  const studyIcid = detail?.study.icid ?? '';
  const { data: encephalonStudy } = useGetEncephaloneStudyDataQuery(studyIcid, {
    skip: !studyIcid,
  });
  useEffect(() => {
    const uuid = encephalonStudy?.results?.[0]?.uuid || null;
    if (uuid && uuid !== currentStudyUuid) {
      setStudyExists(true);
      setCurrentStudyUuid(uuid);
      triggerScanUpdate();
    }
    if (!uuid && currentStudyUuid) {
      setStudyExists(false);
      setCurrentStudyUuid(null);
      setScans([]);
    }
  }, [
    encephalonStudy,
    currentStudyUuid,
    setStudyExists,
    setCurrentStudyUuid,
    setScans,
    triggerScanUpdate,
  ]);

  const mediaUrl: string = dicom?.media_signed_url || '';
  const media = useMemo(() => {
    if (selectedKeypointFrame)
      return {
        signed_url: selectedKeypointFrame.frameUrl,
        extension: selectedKeypointFrame.frameExtension,
        icid: selectedKeypointFrame.frameIcid,
      };
    if (selectedSegmentation)
      return {
        signed_url: selectedSegmentation.frameUrl,
        extension: selectedSegmentation.frameExtension,
        icid: selectedSegmentation.frameIcid,
      };
    return mediaUrl ? { signed_url: mediaUrl, extension: deriveExtension(mediaUrl), icid } : null;
  }, [selectedKeypointFrame, selectedSegmentation, mediaUrl, icid]);

  const downloadStudy = () => {
    if (!studyId) return;
    const token = localStorage.getItem('firebase_id_token');
    const base = (process.env.REACT_APP_API_MAIN_URL || '').replace(/\/$/, '');
    toast.info('Building ZIP… the download starts automatically when ready.', { autoClose: 8000 });
    const a = document.createElement('a');
    a.href = `${base}/api/v1/dicoms/study/${studyId}/download-zip?token=${token}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (dicomError) {
    return (
      <div className="ax-page">
        <Alert
          type="warning"
          showIcon
          message={`DICOM ${icid} not found`}
          description="Check the ICID, or find it from the Studies or DICOMs pages."
        />
      </div>
    );
  }

  const study = detail?.study;
  const reportNumber = (key: string): number | undefined => {
    const m = detail?.report?.measurements.find(x => x.key === key);
    const n = m ? Number(m.value) : NaN;
    return Number.isFinite(n) ? n : undefined;
  };
  const dicomRows: KV[] = dicom
    ? [
        ['ICID', <span className="ax-mono">{dicom.icid}</span>],
        ['Index in study', dicom.index ?? row?.index],
        ['Type', dicom.type],
        ['Manufacturer', dicom.manufacturer],
        ['Model', dicom.manufacturer_model_name],
        ['Frames', fmt(dicom.number_of_frames)],
        [
          'Frame rate',
          dicom.frames_per_second ? `${Number(dicom.frames_per_second).toFixed(1)} fps` : null,
        ],
        [
          'Size',
          dicom.pixel_width && dicom.pixel_height
            ? `${dicom.pixel_width} × ${dicom.pixel_height} px`
            : null,
        ],
        [
          'Pixel spacing',
          dicom.physical_delta_x
            ? `${Number(dicom.physical_delta_x).toFixed(4)} × ${Number(dicom.physical_delta_y ?? 0).toFixed(4)} ${dicom.physical_units_x_direction ?? ''}`.trim()
            : null,
        ],
        [
          'AI view',
          row?.predicted_view
            ? `${row.predicted_view}${row.predicted_confidence != null ? ` (${(100 * row.predicted_confidence).toFixed(0)}%)` : ''}`
            : (dicom.view_prediction ?? []).join(', '),
        ],
        ['Tech view', row?.labeled_view],
        ['Segmentation', row ? yes(row.has_segmentation) : null],
        ['Keypoints', row ? yes(row.has_keypoints) : null],
        ['Quality label', row ? yes(row.has_quality_label) : null],
        [
          'Annotation',
          dicom.skipped_annotations ? <span style={{ color: 'var(--warn)' }}>skipped</span> : null,
        ],
        ['Added', dicom.created_at ? new Date(dicom.created_at).toLocaleString() : null],
      ]
    : [];
  const studyRows: KV[] = study
    ? [
        [
          'Study',
          <Link to={studyHref(study.icid ?? study.uuid)} className="ax-link">
            {study.icid ?? study.uuid}
          </Link>,
        ],
        ['Source', study.datasource],
        ['Foreign id', study.foreign_id],
        ['Visit year', study.visit_year],
        ['Age at visit', study.age_at_visit],
        ['Sex', study.gender],
        ['Race / ethnicity', [study.race, study.ethnicity].filter(Boolean).join(' / ')],
        [
          'DICOMs',
          `${fmt(rail.length)}${study.number_of_usable_dicoms != null ? ` (${fmt(study.number_of_usable_dicoms)} usable)` : ''}`,
        ],
        ['Designation', study.designation],
      ]
    : [];

  return (
    <div className="ax-dicompage">
      <aside className="ax-studyrail" aria-label="DICOMs in this study">
        <div className="head">
          {study ? (
            <>
              <Link to={studyHref(study.icid ?? study.uuid)} className="ax-link">
                {study.icid}
              </Link>
              <span className="ax-faint">
                {fmt(rail.length)} DICOMs{study.datasource ? ` · ${study.datasource}` : ''}
              </span>
            </>
          ) : (
            <Skeleton h={30} />
          )}
        </div>
        <div className="list" role="list">
          {rail.map(d => (
            <RailItem key={d.uuid} d={d} current={d.icid === icid} onPick={go} />
          ))}
        </div>
      </aside>

      <div className="ax-dcenter">
        <div className="ax-dtoolbar">
          <Tooltip title="Previous DICOM (←)">
            <Button
              icon={<LeftOutlined />}
              disabled={!prev}
              onClick={() => go(prev)}
              aria-label="Previous DICOM"
            />
          </Tooltip>
          <div className="title">
            <span className="ax-mono">{icid}</span>
            <span className="ax-faint">
              {pos >= 0 ? `${pos + 1} of ${rail.length} in study` : ''}
            </span>
          </div>
          <Tooltip title="Next DICOM (→)">
            <Button
              icon={<RightOutlined />}
              disabled={!next}
              onClick={() => go(next)}
              aria-label="Next DICOM"
            />
          </Tooltip>
          {row && (
            <span style={{ marginLeft: 8 }}>
              <ViewChips
                predicted={row.predicted_view}
                labeled={row.labeled_view}
                confidence={row.predicted_confidence}
              />
            </span>
          )}
        </div>

        <div className="ax-dviewer">
          {media ? (
            <Viewer
              key={selectedKeypointFrame ? `kp-${selectedKeypointFrame.frameIcid}` : `v-${icid}`}
              mediaData={media}
              dicomDownloadUrl={dicom?.signed_url}
              segmentationData={
                selectedSegmentation
                  ? {
                      icid: selectedSegmentation.maskIcid,
                      paths: selectedSegmentation.paths,
                      frameUrl: selectedSegmentation.frameUrl,
                    }
                  : null
              }
              onClearSegmentation={() => setSelectedSegmentation(null)}
              dicom_Id={dicom?.icid}
              keypoints={selectedKeypointFrame ? selectedKeypointFrame.keypoints : []}
              onClearKeypoints={() => setSelectedKeypointFrame(null)}
              numberOfFrames={dicom?.number_of_frames}
              onDownloadFullStudy={downloadStudy}
              isDownloadingFullStudy={false}
            />
          ) : dicomLoading ? (
            <Skeleton h={420} />
          ) : (
            <Empty title="No rendered media for this DICOM">
              The original DICOM can still be downloaded from the study.
            </Empty>
          )}
        </div>

        {dicom?.uuid && (
          <div className="ax-dpanels">
            <Segments
              defaultExpanded={false}
              mediaUuid={dicom.media_uuid}
              dicomId={dicom.uuid}
              onSegmentationClick={p => {
                setSelectedSegmentation(p);
                setSelectedKeypointFrame(null);
              }}
              onKeypointClick={p => {
                setSelectedKeypointFrame({ ...p });
                setSelectedSegmentation(null);
              }}
            />
            <Prediction dicomId={dicom.uuid} defaultExpanded={false} />
          </div>
        )}
      </div>

      <aside className="ax-dside" aria-label="Details">
        <Section title="DICOM">
          {dicom ? <KVTable rows={dicomRows} /> : <Skeleton h={200} />}
        </Section>
        <Section title="Study">
          {study ? <KVTable rows={studyRows} /> : <Skeleton h={120} />}
        </Section>
        {detail &&
          (detail.report ? (
            <ReportTables report={detail.report} />
          ) : (
            <Section title="Report">
              <p className="ax-faint">No report in the warehouse for this study.</p>
            </Section>
          ))}
        {study?.icid && (
          <Section title="Encephalon">
            <EncephalonSection
              name={study.icid}
              age={
                reportNumber('age') ??
                (study.age_at_visit ? Number(study.age_at_visit) : (undefined as unknown as number))
              }
              height={reportNumber('height') as number}
              weight={reportNumber('weight') as number}
              sex={(study.gender ?? '') as string}
              dicomsInStudy={dicomsInStudy}
            />
          </Section>
        )}
      </aside>
      <ToastContainer position="top-right" autoClose={5000} newestOnTop theme="dark" />
    </div>
  );
};

export default DicomPage;
